import { useState, useEffect } from "react";
import {
    apiService,
    type Experiment,
    type Prompt,
    type ExperimentCreateRequest,
    type PromptVersionStatus,
} from "../services/api.service";
import { APPLICATIONS, DEFAULT_APPLICATION_ID } from "../config/api.config";
import "./ExperimentList.css";

interface VariantInput {
    name: string;
    description: string;
    prompt_version: string;
    weight: number;
}

export const ExperimentList = () => {
    const [experiments, setExperiments] = useState<Experiment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedExperiment, setSelectedExperiment] = useState<Experiment | null>(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    // Create modal state
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [versions, setVersions] = useState<PromptVersionStatus[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        application_id: DEFAULT_APPLICATION_ID,
        prompt_id: "",
        name: "",
        description: "",
        hypothesis: "",
        variants: [
            { name: "Control", description: "Current production version", prompt_version: "", weight: 50 },
            { name: "Treatment", description: "New version to test", prompt_version: "", weight: 50 },
        ] as VariantInput[],
    });

    useEffect(() => {
        loadExperiments();
    }, []);

    useEffect(() => {
        if (showCreateModal) {
            loadPrompts();
        }
    }, [showCreateModal, formData.application_id]);

    useEffect(() => {
        if (formData.prompt_id) {
            loadVersions();
        }
    }, [formData.prompt_id]);

    const loadExperiments = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await apiService.getExperiments();
            setExperiments(data);
        } catch (err) {
            setError("Failed to load experiments");
        } finally {
            setIsLoading(false);
        }
    };

    const loadPrompts = async () => {
        try {
            const data = await apiService.getPrompts(formData.application_id);
            setPrompts(data);
        } catch (err) {
            console.error("Failed to load prompts", err);
        }
    };

    const loadVersions = async () => {
        if (!formData.prompt_id) return;
        const prompt = prompts.find((p) => p.prompt_id === formData.prompt_id);
        if (!prompt) return;
        try {
            const data = await apiService.getPromptVersionStatuses(
                prompt.application_id,
                prompt.prompt_id
            );
            setVersions(data);
        } catch (err) {
            console.error("Failed to load versions", err);
        }
    };

    const getStatusBadgeClass = (status: string) => {
        switch (status) {
            case "running": return "status-running";
            case "paused": return "status-paused";
            case "completed": return "status-completed";
            default: return "status-draft";
        }
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
        });
    };

    const handleStartExperiment = async (experimentId: string) => {
        setIsSaving(true);
        try {
            await apiService.startExperiment(experimentId);
            loadExperiments();
            if (selectedExperiment?.experiment_id === experimentId) {
                const updated = await apiService.getExperiment(experimentId);
                setSelectedExperiment(updated);
            }
        } catch (err) {
            setError("Failed to start experiment");
        } finally {
            setIsSaving(false);
        }
    };

    const handleStopExperiment = async (experimentId: string) => {
        setIsSaving(true);
        try {
            await apiService.stopExperiment(experimentId);
            loadExperiments();
            if (selectedExperiment?.experiment_id === experimentId) {
                const updated = await apiService.getExperiment(experimentId);
                setSelectedExperiment(updated);
            }
        } catch (err) {
            setError("Failed to stop experiment");
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateExperiment = async () => {
        setIsSaving(true);
        setError(null);
        try {
            const request: ExperimentCreateRequest = {
                application_id: formData.application_id,
                prompt_id: formData.prompt_id,
                name: formData.name,
                description: formData.description,
                hypothesis: formData.hypothesis,
                variants: formData.variants.map((v) => ({
                    name: v.name,
                    description: v.description,
                    prompt_version: v.prompt_version,
                    weight: v.weight,
                })),
            };
            await apiService.createExperiment(request);
            setShowCreateModal(false);
            resetForm();
            loadExperiments();
        } catch (err) {
            setError("Failed to create experiment");
        } finally {
            setIsSaving(false);
        }
    };

    const resetForm = () => {
        setFormData({
            application_id: DEFAULT_APPLICATION_ID,
            prompt_id: "",
            name: "",
            description: "",
            hypothesis: "",
            variants: [
                { name: "Control", description: "Current production version", prompt_version: "", weight: 50 },
                { name: "Treatment", description: "New version to test", prompt_version: "", weight: 50 },
            ],
        });
        setVersions([]);
    };

    const updateVariant = (index: number, field: keyof VariantInput, value: string | number) => {
        setFormData((prev) => ({
            ...prev,
            variants: prev.variants.map((v, i) =>
                i === index ? { ...v, [field]: value } : v
            ),
        }));
    };

    const addVariant = () => {
        setFormData((prev) => ({
            ...prev,
            variants: [
                ...prev.variants,
                { name: `Variant ${prev.variants.length + 1}`, description: "", prompt_version: "", weight: 0 },
            ],
        }));
    };

    const removeVariant = (index: number) => {
        if (formData.variants.length <= 2) return;
        setFormData((prev) => ({
            ...prev,
            variants: prev.variants.filter((_, i) => i !== index),
        }));
    };

    const getTotalWeight = () => {
        return formData.variants.reduce((sum, v) => sum + v.weight, 0);
    };

    if (isLoading) {
        return (
            <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading experiments...</p>
            </div>
        );
    }

    if (error && !showCreateModal && !selectedExperiment) {
        return (
            <div className="error-state">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>{error}</p>
                <button onClick={loadExperiments}>Retry</button>
            </div>
        );
    }

    return (
        <div className="experiment-list-container">
            <div className="list-toolbar">
                <div className="filter-group">
                    <span className="experiment-count">
                        {experiments.length} experiment{experiments.length !== 1 ? "s" : ""}
                    </span>
                </div>
                <button className="create-btn" onClick={() => setShowCreateModal(true)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    New Experiment
                </button>
            </div>

            {experiments.length === 0 ? (
                <div className="empty-state">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18" />
                    </svg>
                    <h3>No experiments yet</h3>
                    <p>Create your first A/B test experiment</p>
                    <button onClick={() => setShowCreateModal(true)}>Create Experiment</button>
                </div>
            ) : (
                <div className="experiments-table-wrapper">
                    <table className="experiments-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Prompt</th>
                                <th>Status</th>
                                <th>Variants</th>
                                <th>Assignments</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {experiments.map((exp) => (
                                <tr key={exp.experiment_id}>
                                    <td>
                                        <div className="experiment-name-cell">
                                            <span className="experiment-name">{exp.name}</span>
                                            <span className="experiment-id">{exp.experiment_id}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="prompt-ref">
                                            {exp.application_id}/{exp.prompt_id}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${getStatusBadgeClass(exp.status)}`}>
                                            {exp.status}
                                        </span>
                                    </td>
                                    <td>{exp.variants.length}</td>
                                    <td>{exp.total_assignments}</td>
                                    <td>{formatDate(exp.created_at)}</td>
                                    <td>
                                        <div className="action-buttons">
                                            <button
                                                className="view-btn"
                                                onClick={() => setSelectedExperiment(exp)}
                                            >
                                                View
                                            </button>
                                            {exp.status === "draft" && (
                                                <button
                                                    className="start-btn"
                                                    onClick={() => handleStartExperiment(exp.experiment_id)}
                                                    disabled={isSaving}
                                                >
                                                    Start
                                                </button>
                                            )}
                                            {exp.status === "running" && (
                                                <button
                                                    className="stop-btn"
                                                    onClick={() => handleStopExperiment(exp.experiment_id)}
                                                    disabled={isSaving}
                                                >
                                                    Stop
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Experiment Detail Modal */}
            {selectedExperiment && (
                <div className="modal-overlay">
                    <div className="modal experiment-detail-modal">
                        <div className="modal-header">
                            <div>
                                <h3>{selectedExperiment.name}</h3>
                                <span className="experiment-id-small">{selectedExperiment.experiment_id}</span>
                            </div>
                            <button className="close-btn" onClick={() => setSelectedExperiment(null)}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-grid">
                                <div className="detail-item">
                                    <label>Status</label>
                                    <span className={`status-badge ${getStatusBadgeClass(selectedExperiment.status)}`}>
                                        {selectedExperiment.status}
                                    </span>
                                </div>
                                <div className="detail-item">
                                    <label>Prompt</label>
                                    <span>{selectedExperiment.application_id}/{selectedExperiment.prompt_id}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Total Assignments</label>
                                    <span>{selectedExperiment.total_assignments}</span>
                                </div>
                                <div className="detail-item">
                                    <label>Created</label>
                                    <span>{formatDate(selectedExperiment.created_at)}</span>
                                </div>
                            </div>

                            {selectedExperiment.description && (
                                <div className="detail-section">
                                    <label>Description</label>
                                    <p>{selectedExperiment.description}</p>
                                </div>
                            )}

                            {selectedExperiment.hypothesis && (
                                <div className="detail-section">
                                    <label>Hypothesis</label>
                                    <p>{selectedExperiment.hypothesis}</p>
                                </div>
                            )}

                            <div className="detail-section">
                                <label>Variants</label>
                                <div className="variants-grid">
                                    {selectedExperiment.variants.map((variant) => (
                                        <div key={variant.variant_id} className="variant-card">
                                            <div className="variant-header">
                                                <span className="variant-name">{variant.name}</span>
                                                <span className="variant-weight">{variant.weight}%</span>
                                            </div>
                                            <p className="variant-description">{variant.description}</p>
                                            <div className="variant-version">
                                                Version: <strong>v{variant.prompt_version}</strong>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            {selectedExperiment.status === "draft" && (
                                <button
                                    className="start-btn"
                                    onClick={() => handleStartExperiment(selectedExperiment.experiment_id)}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "..." : "Start Experiment"}
                                </button>
                            )}
                            {selectedExperiment.status === "running" && (
                                <button
                                    className="stop-btn"
                                    onClick={() => handleStopExperiment(selectedExperiment.experiment_id)}
                                    disabled={isSaving}
                                >
                                    {isSaving ? "..." : "Stop Experiment"}
                                </button>
                            )}
                            <button className="cancel-btn" onClick={() => setSelectedExperiment(null)}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Experiment Modal */}
            {showCreateModal && (
                <div className="modal-overlay">
                    <div className="modal create-experiment-modal">
                        <div className="modal-header">
                            <h3>Create New Experiment</h3>
                            <button className="close-btn" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="modal-body">
                            {error && (
                                <div className="error-banner">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <line x1="12" y1="8" x2="12" y2="12" />
                                        <line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                    {error}
                                </div>
                            )}

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Application</label>
                                    <select
                                        value={formData.application_id}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev,
                                            application_id: e.target.value,
                                            prompt_id: "",
                                        }))}
                                    >
                                        {APPLICATIONS.map((app) => (
                                            <option key={app.value} value={app.value}>{app.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Prompt</label>
                                    <select
                                        value={formData.prompt_id}
                                        onChange={(e) => setFormData((prev) => ({
                                            ...prev,
                                            prompt_id: e.target.value,
                                        }))}
                                    >
                                        <option value="">Select a prompt...</option>
                                        {prompts.map((p) => (
                                            <option key={p.prompt_id} value={p.prompt_id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Experiment Name</label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g., Test new math scaffolding approach"
                                />
                            </div>

                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                    placeholder="What are you testing?"
                                    rows={2}
                                />
                            </div>

                            <div className="form-group">
                                <label>Hypothesis</label>
                                <textarea
                                    value={formData.hypothesis}
                                    onChange={(e) => setFormData((prev) => ({ ...prev, hypothesis: e.target.value }))}
                                    placeholder="What do you expect to happen?"
                                    rows={2}
                                />
                            </div>

                            <div className="form-section">
                                <div className="section-header">
                                    <h4>Variants</h4>
                                    <div className="weight-indicator">
                                        Total: {getTotalWeight()}%
                                        {getTotalWeight() !== 100 && (
                                            <span className="weight-warning"> (should be 100%)</span>
                                        )}
                                    </div>
                                </div>

                                <div className="variants-form">
                                    {formData.variants.map((variant, index) => (
                                        <div key={index} className="variant-row">
                                            <div className="variant-inputs">
                                                <input
                                                    type="text"
                                                    value={variant.name}
                                                    onChange={(e) => updateVariant(index, "name", e.target.value)}
                                                    placeholder="Variant name"
                                                    className="variant-name-input"
                                                />
                                                <input
                                                    type="text"
                                                    value={variant.description}
                                                    onChange={(e) => updateVariant(index, "description", e.target.value)}
                                                    placeholder="Description"
                                                    className="variant-desc-input"
                                                />
                                                <select
                                                    value={variant.prompt_version}
                                                    onChange={(e) => updateVariant(index, "prompt_version", e.target.value)}
                                                    className="variant-version-select"
                                                >
                                                    <option value="">Select version...</option>
                                                    {versions.map((v) => (
                                                        <option key={v.version} value={v.version}>
                                                            v{v.version} ({v.status})
                                                        </option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="number"
                                                    value={variant.weight}
                                                    onChange={(e) => updateVariant(index, "weight", Number(e.target.value))}
                                                    min={0}
                                                    max={100}
                                                    className="variant-weight-input"
                                                />
                                                <span className="weight-suffix">%</span>
                                            </div>
                                            {formData.variants.length > 2 && (
                                                <button
                                                    type="button"
                                                    className="remove-variant-btn"
                                                    onClick={() => removeVariant(index)}
                                                >
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18" />
                                                        <line x1="6" y1="6" x2="18" y2="18" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <button type="button" className="add-variant-btn" onClick={addVariant}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    Add Variant
                                </button>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="cancel-btn" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                                Cancel
                            </button>
                            <button
                                className="save-btn"
                                onClick={handleCreateExperiment}
                                disabled={
                                    isSaving ||
                                    !formData.name ||
                                    !formData.prompt_id ||
                                    getTotalWeight() !== 100 ||
                                    formData.variants.some((v) => !v.prompt_version)
                                }
                            >
                                {isSaving ? "Creating..." : "Create Experiment"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
